import { useMutation, UseMutationResult } from "@tanstack/react-query";
import { uploadRequest, uploadZipFile } from "@/lib/dataAccess/apiService";
import { useBoundStore } from "../stores/useBoundStore";
import toast from "react-hot-toast";
import { UploadResponseUpload } from "@clients/velys-infrastructure/api";
import { useAuthStore } from "../stores/useAuthStore";

type UploadMutationVariables = {
  file: File;
  encryptKey: string;
  location_name: string;
};

export type UploadMutationType = UseMutationResult<
  UploadResponseUpload | undefined,
  Error,
  UploadMutationVariables
>;

export function useCaseUpload() {
  const setCaseUploadStatus = useBoundStore(
    (state) => state.setCaseUploadStatus
  );
  const setUploadProgress = useBoundStore((state) => state.setUploadProgress);
  const user_profile = useAuthStore((state) => state.user_profile);
  const selectedOrgId = useBoundStore((state) => state.selectedOrgId);

  const uploadMutation = useMutation({
    mutationFn: async ({
      file,
      encryptKey,
      location_name,
    }: {
      file: File;
      encryptKey: string;
      location_name: string;
    }) => {
      if (!selectedOrgId) {
        toast.error("Organization ID is missing.");
        throw new Error("Organization ID is missing.");
      }

      const uploadObject = {
        upload: {
          org_id: selectedOrgId,
          zip_filename: file.name,
          location_name,
          zip_enc_password: encryptKey,
          surgeon_npi: user_profile?.surgeon_npi.toString() || "",
        },
      };
      const response = await uploadRequest(selectedOrgId, uploadObject);
      const presignedURL = response?.pre_signed_url;
      const uploadStatus = response?.upload;

      if (!presignedURL || !uploadStatus || !user_profile) {
        toast.error("Error getting upload request");
        throw new Error("Error getting upload request");
      }

      setCaseUploadStatus(uploadStatus);
      const uploadResponse = await uploadZipFile(
        presignedURL,
        file,
        uploadObject.upload,
        response,
        (percent) => setUploadProgress(percent)
      );
      if (!uploadResponse) {
        toast.error("Upload failed");
        throw new Error("Upload failed");
      }
      return response.upload;
    },
  });

  return {
    uploadMutation,
  };
}
